/** A stretch of time the cache holds in full, in unix seconds. */
export interface PollSpan {
  oldest: number
  newest: number
}

/**
 * Edges of what the cache holds for one polled site, location or presence, in
 * unix seconds. heavyOldest and heavyNewest are the newest span, the one polled
 * live and backfilled; olderSpans are the spans behind it, newest first, with a
 * gap before each.
 */
export interface PollCursor {
  lightSyncedTo: number
  heavyNewest: number
  heavyOldest: number
  backfillWindow: number
  olderSpans?: PollSpan[]
  /**
   * Only lightSyncedTo holds: a presence whose sessions no page asked for
   * keeps its light lists current with it and caches no heavy data under it.
   */
  lightOnly?: true
}

export interface PollCursorState extends PollCursor {
  backfilling: boolean
}

export enum PollKind {
  Site = 'site',
  Location = 'location',
  Presence = 'presence',
  SiteLog = 'site-log',
}

export enum PollRequestKind {
  FreshLight = 'freshLight',
  FreshHeavy = 'freshHeavy',
  Active = 'active',
  Light = 'light',
  LiveHeavy = 'liveHeavy',
  NewSpan = 'newSpan',
  Backfill = 'backfill',
}

export interface PollRequest {
  kind: PollRequestKind
  ifRange?: number
  start?: number
  end?: number
  /** The api's opaque token for the next page, and where that page's Range starts. */
  continuation?: string
  rangeStart?: number
}

/** The paging fields of a reply's extra. */
export interface PollExtra {
  hasMore?: boolean
  syncedTo?: number
  continuation?: string
}

/** Every span of a cursor, newest first. */
export function pollSpans(cursor: PollCursor): PollSpan[] {
  return [
    { oldest: cursor.heavyOldest, newest: cursor.heavyNewest },
    ...(cursor.olderSpans ?? []),
  ]
}

/**
 * The union of spans, newest first: spans that overlap or touch become one.
 */
export function joinPollSpans(spans: PollSpan[]): PollSpan[] {
  const sorted = [...spans].sort((a, b) => b.newest - a.newest)
  const joined: PollSpan[] = []
  for (const span of sorted) {
    const last = joined[joined.length - 1]
    if (last && span.newest >= last.oldest) {
      last.oldest = Math.min(last.oldest, span.oldest)
    } else {
      joined.push({ ...span })
    }
  }
  return joined
}

/** The cursor with spans: the newest becomes heavyOldest and heavyNewest. */
function withPollSpans(cursor: PollCursor, spans: PollSpan[]): PollCursor {
  const [newest, ...older] = joinPollSpans(spans)
  const next: PollCursor = {
    lightSyncedTo: cursor.lightSyncedTo,
    heavyNewest: newest.newest,
    heavyOldest: newest.oldest,
    backfillWindow: cursor.backfillWindow,
  }
  if (older.length > 0) {
    next.olderSpans = older
  }
  return next
}

/**
 * Cursors only grow: lightSyncedTo takes the larger value, and the spans of
 * both are joined, so a span is never stretched over a gap.
 */
export function mergePollCursors(
  stored: PollCursor | undefined,
  cursor: PollCursor
): PollCursor {
  if (!stored) {
    return cursor
  }

  // A light-only cursor adds its lightSyncedTo, never a span.
  if (stored.lightOnly || cursor.lightOnly) {
    const kept = cursor.lightOnly && !stored.lightOnly ? stored : cursor
    return {
      ...kept,
      lightSyncedTo: Math.max(stored.lightSyncedTo, cursor.lightSyncedTo),
    }
  }

  return withPollSpans(
    {
      ...cursor,
      lightSyncedTo: Math.max(stored.lightSyncedTo, cursor.lightSyncedTo),
    },
    [...pollSpans(stored), ...pollSpans(cursor)]
  )
}

/**
 * The cursor without the spans behind the first gap over maxGap: they are too
 * far off to fill, and are dropped with their rows. dropped holds those spans,
 * undefined when none is dropped.
 */
export function dropFarPollSpans(
  cursor: PollCursor,
  maxGap: number = 7 * 24 * 60 * 60
): { cursor: PollCursor; dropped?: PollSpan[] } {
  const spans = pollSpans(cursor)
  const far = spans.findIndex(
    (span, i) => i > 0 && spans[i - 1].oldest - span.newest > maxGap
  )
  if (far === -1) {
    return { cursor: cursor }
  }

  return {
    cursor: withPollSpans(cursor, spans.slice(0, far)),
    dropped: spans.slice(far),
  }
}

/** The cursor of light lists alone, synced to lightSyncedTo. */
export function lightOnlyPollCursor(lightSyncedTo: number): PollCursor {
  return {
    lightSyncedTo: lightSyncedTo,
    heavyNewest: lightSyncedTo,
    heavyOldest: lightSyncedTo,
    backfillWindow: 0,
    lightOnly: true,
  }
}

/** Today's string cursor means the cache is complete up to it. */
export function legacyPollCursor(
  etag: string,
  backfillWindow: number = 24 * 60 * 60
): PollCursor | undefined {
  const t = Math.floor(new Date(etag).getTime() / 1000)
  if (isNaN(t)) {
    return undefined
  }

  return {
    lightSyncedTo: t,
    heavyNewest: t,
    heavyOldest: 0,
    backfillWindow: backfillWindow,
  }
}

/** Halves the window after a large reply and doubles it after a small one. */
export function stepBackfillWindow(
  backfillWindow: number,
  entries: number,
  largeReply: number = 2000,
  smallReply: number = 500
): number {
  if (entries >= largeReply) {
    return Math.max(Math.floor(backfillWindow / 2), 1)
  }
  if (entries < smallReply) {
    return backfillWindow * 2
  }
  return backfillWindow
}

/** No cursor yet: the light lists in full, without If-Range. */
export function freshLightPollRequest(): PollRequest {
  return { kind: PollRequestKind.FreshLight }
}

/**
 * No cursor yet: every active session, whatever its age, in one request
 * (sessions=active). The first window behind it is a backfill.
 */
export function activePollRequest(): PollRequest {
  return { kind: PollRequestKind.Active }
}

/**
 * The backfill depth a cursor always reaches: one first window behind
 * heavyNewest, or depth when that is further back.
 */
export function firstWindowDepth(
  cursor: PollCursor,
  depth?: number,
  firstWindow: number = 24 * 60 * 60
): number {
  const first = cursor.heavyNewest - firstWindow
  return depth === undefined ? first : Math.min(depth, first)
}

/** The pause before the next backfill request, so a backfill is a trickle on the api. */
export const BACKFILL_PACE_MS = 1000

/** Items per page of a backfill: small pages, paced, spread the load. */
export const BACKFILL_PAGE_SIZE = 250

/**
 * A request that loads history: a backfill, the window of a fresh start or
 * a new span, or any page after the first of a request.
 */
export function isHistoryRequest(request: PollRequest): boolean {
  return (
    request.kind === PollRequestKind.Backfill ||
    request.kind === PollRequestKind.FreshHeavy ||
    (request.kind === PollRequestKind.NewSpan && request.start !== undefined) ||
    (request.rangeStart ?? 0) > 0
  )
}

/**
 * How long a poll waits before it sends request: the first history page
 * since the poll started goes at once, every later one waits until pace has
 * passed since the previous request. Light and active go at once.
 */
export function pollPace(
  request: PollRequest,
  sinceLast: number,
  first: boolean,
  pace: number = BACKFILL_PACE_MS
): number {
  if (!isHistoryRequest(request) || first) {
    return 0
  }

  return Math.max(0, pace - sinceLast)
}

/**
 * Where the automatic backfill goes next: always one first window behind
 * heavyNewest, then the gap to the nearest older span, then on one backfill
 * window at a time until the cache holds maxEntries or reaches maxDepth,
 * whichever comes first, never past created. Undefined once it is there.
 * Pages needing older data backfill themselves.
 */
export function autoBackfillTo(
  cursor: PollCursor,
  cached: number,
  created: number = 0,
  firstWindow: number = 24 * 60 * 60,
  maxDepth: number = 72 * 60 * 60,
  maxEntries: number = 10000
): number | undefined {
  const first = Math.max(cursor.heavyNewest - firstWindow, created)
  if (cursor.heavyOldest > first) {
    return first
  }

  const older = cursor.olderSpans?.[0]
  if (older) {
    return Math.max(cursor.heavyOldest - cursor.backfillWindow, older.newest)
  }

  const deepest = Math.max(cursor.heavyNewest - maxDepth, created)
  if (cached >= maxEntries || cursor.heavyOldest <= deepest) {
    return undefined
  }

  return Math.max(cursor.heavyOldest - cursor.backfillWindow, deepest)
}

/** No cursor yet: the window of heavy that ends at the light reply. */
export function freshHeavyPollRequest(
  lightSyncedTo: number,
  firstWindow: number = 24 * 60 * 60
): PollRequest {
  return {
    kind: PollRequestKind.FreshHeavy,
    start: lightSyncedTo - firstWindow,
  }
}

export function lightPollRequest(cursor: PollCursor): PollRequest {
  return { kind: PollRequestKind.Light, ifRange: cursor.lightSyncedTo }
}

/**
 * Live heavy, or a new span when heavyNewest is more than firstWindow behind
 * lightSyncedTo, the server's latest Last-Modified: the newest data first, the
 * gap behind it filled by backfill. With window, the new span is the window of
 * heavy ending at lightSyncedTo (the site log); without, every active session
 * (the session polls, as on a fresh start).
 */
export function heavyPollRequest(
  cursor: PollCursor,
  window: boolean = false,
  firstWindow: number = 24 * 60 * 60
): PollRequest {
  if (cursor.lightSyncedTo - cursor.heavyNewest > firstWindow) {
    return window
      ? {
          kind: PollRequestKind.NewSpan,
          start: cursor.lightSyncedTo - firstWindow,
        }
      : { kind: PollRequestKind.NewSpan }
  }

  return { kind: PollRequestKind.LiveHeavy, ifRange: cursor.heavyNewest }
}

/**
 * The next window back towards depth, or undefined once heavyOldest has
 * reached it. A window ends at the nearest older span, which it then joins.
 */
export function backfillPollRequest(
  cursor: PollCursor,
  depth: number
): PollRequest | undefined {
  if (cursor.heavyOldest <= depth) {
    return undefined
  }

  return {
    kind: PollRequestKind.Backfill,
    ifRange: cursor.lightSyncedTo,
    start: Math.max(
      cursor.heavyOldest - cursor.backfillWindow,
      depth,
      cursor.olderSpans?.[0]?.newest ?? depth
    ),
    end: cursor.heavyOldest,
  }
}

/**
 * The Range of a request: light and active in full, history in pages of
 * BACKFILL_PAGE_SIZE, other heavy requests in pages of pageSize.
 */
export function pollRange(
  request: PollRequest,
  pageSize: number = isHistoryRequest({ ...request, rangeStart: undefined })
    ? BACKFILL_PAGE_SIZE
    : 1000
): string {
  if (
    request.kind === PollRequestKind.FreshLight ||
    request.kind === PollRequestKind.Light ||
    request.kind === PollRequestKind.Active ||
    (request.kind === PollRequestKind.NewSpan && request.start === undefined)
  ) {
    return 'items=0-'
  }
  const start = request.rangeStart ?? 0
  return 'items=' + start + '-' + (start + pageSize - 1)
}

/**
 * The next page of page's request: the same request with the api's
 * continuation and a Range starting after the rows received so far.
 * Undefined when extra says there is no more, and when the api says there is
 * more but sent no rows or no token (a stalled run: the window starts again
 * at Range 0 on the next poll).
 */
export function nextPollPageRequest(
  page: PollRequest,
  extra: PollExtra | undefined,
  received: number
): PollRequest | undefined {
  if (!extra?.hasMore || !extra.continuation || received <= 0) {
    return undefined
  }

  return {
    ...page,
    continuation: extra.continuation,
    rangeStart: (page.rangeStart ?? 0) + received,
  }
}

/** True when the api says there is more but the page cannot be followed. */
export function isStalledPollPage(
  extra: PollExtra | undefined,
  received: number
): boolean {
  return !!extra?.hasMore && (!extra.continuation || received <= 0)
}

/** The cursor after a reply to request, with lastModified from the server. */
export function nextPollCursor(
  cursor: PollCursor | undefined,
  request: PollRequest,
  lastModified: number,
  entries: number,
  largeReply?: number,
  smallReply?: number
): PollCursor {
  return dropFarPollSpans(
    nextPollCursorSpans(
      cursor,
      request,
      lastModified,
      entries,
      largeReply,
      smallReply
    )
  ).cursor
}

function nextPollCursorSpans(
  cursor: PollCursor | undefined,
  request: PollRequest,
  lastModified: number,
  entries: number,
  largeReply?: number,
  smallReply?: number
): PollCursor {
  if (request.kind === PollRequestKind.Active) {
    return {
      lightSyncedTo: Math.max(cursor?.lightSyncedTo ?? 0, lastModified),
      heavyNewest: lastModified,
      heavyOldest: lastModified,
      backfillWindow: cursor?.backfillWindow ?? 24 * 60 * 60,
    }
  }

  if (request.kind === PollRequestKind.FreshHeavy || !cursor) {
    const heavyOldest = Math.min(request.start ?? lastModified, lastModified)
    return {
      lightSyncedTo: lastModified,
      heavyNewest: lastModified,
      heavyOldest: heavyOldest,
      backfillWindow: stepBackfillWindow(
        Math.max(lastModified - heavyOldest, 1),
        entries,
        largeReply,
        smallReply
      ),
    }
  }

  if (request.kind === PollRequestKind.NewSpan) {
    const fresh =
      request.start === undefined
        ? nextPollCursor(
            undefined,
            { kind: PollRequestKind.Active },
            lastModified,
            entries
          )
        : nextPollCursor(
            undefined,
            { kind: PollRequestKind.FreshHeavy, start: request.start },
            lastModified,
            entries,
            largeReply,
            smallReply
          )
    return withPollSpans(
      {
        ...fresh,
        lightSyncedTo: Math.max(cursor.lightSyncedTo, lastModified),
        backfillWindow:
          request.start === undefined
            ? cursor.backfillWindow
            : fresh.backfillWindow,
      },
      [
        { oldest: fresh.heavyOldest, newest: fresh.heavyNewest },
        ...pollSpans(cursor),
      ]
    )
  }

  switch (request.kind) {
    case PollRequestKind.FreshLight:
    case PollRequestKind.Light:
      return { ...cursor, lightSyncedTo: lastModified }
    case PollRequestKind.LiveHeavy:
      return {
        ...cursor,
        lightSyncedTo: lastModified,
        heavyNewest: lastModified,
      }
    case PollRequestKind.Backfill: {
      const next = {
        ...cursor,
        lightSyncedTo: lastModified,
        heavyOldest: request.start ?? cursor.heavyOldest,
        backfillWindow: stepBackfillWindow(
          cursor.backfillWindow,
          entries,
          largeReply,
          smallReply
        ),
      }
      return withPollSpans(next, pollSpans(next))
    }
  }
}
